import React, { useState } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Download, 
  Eye, 
  Heart, 
  Star, 
  ChevronLeft, 
  ShoppingBag, 
  Check, 
  ZoomIn, 
  ZoomOut, 
  Maximize2, 
  Share2, 
  ShieldCheck, 
  Layers, 
  FileCode, 
  Type, 
  Sliders,
  Sparkles,
  GitBranch,
  GitPullRequest,
  Clock,
  CheckCircle2,
  X
} from 'lucide-react';
import { 
  fetchTemplate, 
  requestDownload, 
  forkTemplate, 
  submitMergeRequest, 
  fetchMergeRequests 
} from '../api/client';
import Button from '../components/Button';
import Badge from '../components/Badge';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import LikeButton from '../components/LikeButton';
import StarRating from '../components/StarRating';
import CommentSection from '../components/CommentSection';
import ColorPaletteExtractor from '../components/ColorPaletteExtractor';
import ShareModal from '../components/ShareModal';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=1200';

export default function TemplateDetail() {
  const { slug } = useParams();
  const { setAuthOpen } = useOutletContext();
  const { user, session } = useAuth();
  const { addItem, items, setIsCartOpen } = useCart();
  
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState('commercial');
  const [activeTab, setActiveTab] = useState('specs');
  const [toastMessage, setToastMessage] = useState(null);

  // Fork & Merge Request State
  const [forking, setForking] = useState(false);
  const [mrModalOpen, setMrModalOpen] = useState(false);
  const [mrForm, setMrForm] = useState({ title: '', description: '', diff_notes: '' });
  const [submittingMr, setSubmittingMr] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['template', slug],
    queryFn: () => fetchTemplate(slug),
  });

  const template = data?.template;
  const isFree = template?.is_free === 1 || template?.is_free === true || template?.price_cents === null || template?.price_cents === 0;
  const isInCart = template ? items.some(i => (i.template_id === template.id || i.id === template.id)) : false;

  const basePrice = template?.price_cents ? template.price_cents / 100 : 24;
  const computedPrice = selectedLicense === 'extended' ? basePrice * 2.5 : selectedLicense === 'personal' ? basePrice * 0.75 : basePrice;

  const handleDownloadClick = async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }

    setDownloading(true);
    setDownloadError('');

    try {
      const resData = await requestDownload(template.id);
      window.open(resData.downloadUrl, '_blank');
    } catch (err) {
      console.error(err);
      setDownloadError(err.message || 'Failed to generate download link. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleAddToCart = () => {
    if (!template) return;
    addItem({ ...template, selectedLicense, price_cents: Math.round(computedPrice * 100) });
  };

  const handleBuyNow = () => {
    if (!template) return;
    if (!isInCart) {
      addItem({ ...template, selectedLicense, price_cents: Math.round(computedPrice * 100) });
    }
    setIsCartOpen(true);
  };

  const handleFork = async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setForking(true);
    try {
      const res = await forkTemplate(template.id);
      setToastMessage(res.message || `✓ Forked "${template.title}" to your creator assets!`);
    } catch (err) {
      setToastMessage(err.message || 'Failed to fork template');
    } finally {
      setForking(false);
      setTimeout(() => setToastMessage(null), 3500);
    }
  };

  const handleSubmitMr = async (e) => {
    e.preventDefault();
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setSubmittingMr(true);
    try {
      const res = await submitMergeRequest(template.id, mrForm);
      setMrModalOpen(false);
      setToastMessage(res.message || '✓ Merge Request submitted to original designer!');
      setMrForm({ title: '', description: '', diff_notes: '' });
    } catch (err) {
      setToastMessage(err.message || 'Failed to submit merge request');
    } finally {
      setSubmittingMr(false);
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container" style={{ padding: '80px 0', textAlign: 'center' }}>
        <div className="spinner" />
        <p style={{ color: 'var(--text-secondary)' }}>Loading asset specifications...</p>
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="page-container" style={{ padding: '80px 0', textAlign: 'center' }}>
        <h2 className="text-h2" style={{ marginBottom: '16px' }}>Template not found</h2>
        <Link to="/browse">
          <Button variant="secondary">Back to browse</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ paddingBottom: '100px', paddingTop: '16px' }}>
      
      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: 'var(--bg-surface-active)',
          border: '1px solid var(--primary)',
          color: '#FFFFFF',
          padding: '12px 20px',
          borderRadius: '14px',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: 'fadeIn 0.2s ease'
        }}>
          <Sparkles size={16} color="var(--accent)" />
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{toastMessage}</span>
        </div>
      )}

      {/* Breadcrumb & Action Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 20px', flexWrap: 'wrap', gap: '12px' }}>
        <Link
          to="/browse"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-secondary)',
            fontSize: '0.875rem',
            textDecoration: 'none',
            fontWeight: 600
          }}
        >
          <ChevronLeft size={16} /> Back to marketplace
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={handleFork}
            disabled={forking}
            className="tactile-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '10px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <GitBranch size={14} /> {forking ? 'Forking...' : 'Fork Template'}
          </button>

          <button
            onClick={() => user ? setMrModalOpen(true) : setAuthOpen(true)}
            className="tactile-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '10px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <GitPullRequest size={14} /> Merge Request
          </button>

          <button
            onClick={() => setShareModalOpen(true)}
            className="tactile-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '10px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      {/* Main Two-Column Layout */}
      <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', marginBottom: '64px' }}>

        {/* LEFT COLUMN: Deep Visual Inspection Station */}
        <div style={{ flex: '1 1 540px', minWidth: 0 }}>
          
          {/* Zoomable Canvas Preview */}
          <div style={{
            position: 'relative',
            borderRadius: '24px',
            overflow: 'hidden',
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--shadow-lg)',
            marginBottom: '20px'
          }}>
            <div style={{
              overflow: 'hidden',
              cursor: zoomLevel > 1 ? 'grab' : 'zoom-in',
              aspectRatio: '16/10',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#07070D'
            }}>
              <img
                src={template.preview_url || PLACEHOLDER}
                alt={template.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: `scale(${zoomLevel})`,
                  transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
                onClick={() => setZoomLevel(prev => prev >= 2 ? 1 : prev + 0.5)}
                onError={e => { e.target.src = PLACEHOLDER; }}
              />
            </div>

            {/* Inspection Controls Floating Bar */}
            <div style={{
              position: 'absolute',
              bottom: '16px',
              right: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(9, 9, 16, 0.85)',
              backdropFilter: 'blur(12px)',
              padding: '6px 10px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.12)'
            }}>
              <button
                type="button"
                onClick={() => setZoomLevel(prev => Math.max(1, prev - 0.25))}
                style={{ background: 'none', border: 'none', color: '#FFFFFF', cursor: 'pointer', padding: '4px' }}
                title="Zoom Out"
              >
                <ZoomOut size={16} />
              </button>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#FFFFFF', minWidth: '36px', textAlign: 'center' }}>
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoomLevel(prev => Math.min(2.5, prev + 0.25))}
                style={{ background: 'none', border: 'none', color: '#FFFFFF', cursor: 'pointer', padding: '4px' }}
                title="Zoom In"
              >
                <ZoomIn size={16} />
              </button>
              <div style={{ width: '1px', height: '14px', background: 'rgba(255,255,255,0.2)', margin: '0 2px' }} />
              <button
                type="button"
                onClick={() => setZoomLevel(1)}
                style={{ background: 'none', border: 'none', color: '#FFFFFF', cursor: 'pointer', padding: '4px' }}
                title="Reset Zoom"
              >
                <Maximize2 size={14} />
              </button>
            </div>
          </div>

          {/* Color Palette Swatches */}
          <ColorPaletteExtractor
            colors={['#7B3FFF', '#3BF686', '#090910', '#1C1C2E', '#F4F4FF', '#4F8EFF']}
            title="Design System Color Palette"
          />

          {/* Detailed Tabs: Specs, Typography, Layers */}
          <div style={{
            background: 'var(--bg-surface)',
            borderRadius: '20px',
            border: '1px solid var(--border-subtle)',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {[
                { id: 'specs', label: 'Technical Specs', icon: Sliders },
                { id: 'versions', label: `Versions (${template.versions?.length || 1})`, icon: Clock },
                { id: 'typography', label: 'Typography', icon: Type },
                { id: 'layers', label: 'Layer Hierarchy', icon: Layers }
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    background: activeTab === t.id ? 'var(--bg-surface-active)' : 'transparent',
                    color: activeTab === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: activeTab === t.id ? 700 : 500,
                    fontSize: '0.8125rem',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <t.icon size={14} />
                  {t.label}
                </button>
              ))}
            </div>

            {activeTab === 'versions' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(template.versions || [
                  { id: 'v-1', version_number: 1, changelog: 'Initial production release with verified design tokens.', is_latest: 1, created_at: template.created_at }
                ]).map(ver => (
                  <div key={ver.id} style={{
                    background: 'var(--bg-base)',
                    padding: '16px',
                    borderRadius: '14px',
                    border: '1px solid var(--border-subtle)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                          v{ver.version_number}.0
                        </span>
                        {ver.is_latest === 1 && (
                          <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: 'rgba(16, 185, 129, 0.15)', color: '#10B981', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                            Current Latest
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                        {ver.created_at ? new Date(ver.created_at).toLocaleDateString() : 'Production'}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                      {ver.changelog || 'Verified design assets and vector layers included.'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'specs' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', fontSize: '0.875rem' }}>
                <div style={{ background: 'var(--bg-base)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', display: 'block' }}>Resolution</span>
                  <strong>3840 × 2160 (4K UHD)</strong>
                </div>
                <div style={{ background: 'var(--bg-base)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', display: 'block' }}>DPI / Color Space</span>
                  <strong>300 DPI · Display P3 / sRGB</strong>
                </div>
                <div style={{ background: 'var(--bg-base)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', display: 'block' }}>Vectors & Scalability</span>
                  <strong>100% Fully Vector Shape Layers</strong>
                </div>
                <div style={{ background: 'var(--bg-base)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', display: 'block' }}>Documentation</span>
                  <strong>Comprehensive Setup Guide (.pdf)</strong>
                </div>
              </div>
            )}

            {activeTab === 'typography' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ background: 'var(--bg-base)', padding: '14px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <strong>Plus Jakarta Sans</strong>
                    <span style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 700 }}>Google Fonts (Free)</span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Weights: Regular 400, Medium 500, SemiBold 600, Bold 700</p>
                </div>
                <div style={{ background: 'var(--bg-base)', padding: '14px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <strong>Sora Display</strong>
                    <span style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 700 }}>Google Fonts (Free)</span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Weights: ExtraBold 800 for high-impact headlines</p>
                </div>
              </div>
            )}

            {activeTab === 'layers' && (
              <div style={{ background: 'var(--bg-base)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-subtle)', fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                <div style={{ color: 'var(--primary-text)', marginBottom: '6px' }}>📁 01_HERO_PRESENTATION</div>
                <div style={{ paddingLeft: '16px', color: 'var(--text-secondary)', marginBottom: '4px' }}>└ 📁 UI_Components_AutoLayout</div>
                <div style={{ paddingLeft: '32px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>├ 🖼️ Device_Frames_SmartObjects</div>
                <div style={{ paddingLeft: '32px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>├ 🎨 Gradient_Mesh_Backgrounds</div>
                <div style={{ paddingLeft: '32px', color: 'var(--text-tertiary)' }}>└ 🔤 Typography_Tokens</div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Info, License Picker & Purchase */}
        <div style={{ flex: '1 1 360px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Title + Designer Badge */}
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {template.formats?.map(fmt => (
                <Badge key={fmt} variant="format">{fmt}</Badge>
              ))}
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-subtle)', padding: '3px 8px', borderRadius: '6px' }}>
                Verified Asset
              </span>
            </div>

            <h1 className="text-h1" style={{ marginBottom: '10px' }}>{template.title}</h1>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--primary)', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.75rem' }}>
                {template.designer_name?.charAt(0) || 'D'}
              </div>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Crafted by</span>
              <Link
                to={`/designer/${template.designer_name}`}
                style={{ fontWeight: 700, color: 'var(--text-primary)', textDecoration: 'none' }}
              >
                {template.designer_name}
              </Link>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', padding: '14px 18px', borderRadius: '14px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
            <StarRating templateId={template.id} initialRating={template.avg_rating || 4.9} ratingCount={template.rating_count || 38} />
            <div style={{ width: '1px', height: '20px', background: 'var(--border-subtle)' }} />
            <LikeButton templateId={template.id} initialLikes={template.like_count || 124} />
            <div style={{ width: '1px', height: '20px', background: 'var(--border-subtle)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
              <Download size={14} /> {(template.download_count || 512).toLocaleString()}
            </div>
          </div>

          {/* Description */}
          {template.description && (
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '0.9375rem' }}>
              {template.description}
            </p>
          )}

          {/* Commercial License Matrix (For Paid Assets) */}
          {!isFree && (
            <div>
              <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '10px' }}>
                Select License Tier
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { id: 'commercial', title: 'Standard Commercial', price: `$${basePrice.toFixed(2)}`, desc: '1 commercial client project & personal use' },
                  { id: 'extended', title: 'Extended Commercial', price: `$${(basePrice * 2.5).toFixed(2)}`, desc: 'Unlimited commercial projects & broadcast' }
                ].map(lic => (
                  <button
                    key={lic.id}
                    type="button"
                    onClick={() => setSelectedLicense(lic.id)}
                    className="tactile-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      background: selectedLicense === lic.id ? 'var(--bg-surface-active)' : 'var(--bg-surface)',
                      border: `1.5px solid ${selectedLicense === lic.id ? 'var(--primary)' : 'var(--border-subtle)'}`,
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{lic.title}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{lic.desc}</div>
                    </div>
                    <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--primary-text)' }}>{lic.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Price & Primary Purchase Card */}
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '20px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '18px' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block' }}>Total Price</span>
                <span style={{ fontSize: '2rem', fontWeight: 800, color: isFree ? 'var(--accent)' : 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                  {isFree ? 'Free Download' : `$${computedPrice.toFixed(2)}`}
                </span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ShieldCheck size={14} color="var(--accent)" /> Instant Delivery
              </span>
            </div>

            {isFree ? (
              <Button
                variant="primary"
                onClick={handleDownloadClick}
                disabled={downloading}
                style={{ width: '100%', padding: '16px' }}
              >
                <Download size={18} />
                {downloading ? 'Preparing Download...' : 'Download Free Asset'}
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleAddToCart}
                  className="tactile-btn"
                  style={{
                    flex: 1,
                    padding: '14px 18px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-strong)',
                    background: 'var(--bg-base)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <ShoppingBag size={17} />
                  {isInCart ? 'In Cart' : 'Add to Cart'}
                </button>

                <Button
                  variant="primary"
                  onClick={handleBuyNow}
                  style={{ flex: 1, padding: '14px 18px' }}
                >
                  Buy Now
                </Button>
              </div>
            )}

            {downloadError && (
              <div style={{ marginTop: '12px', fontSize: '0.8125rem', color: 'var(--status-error)', textAlign: 'center' }}>
                {downloadError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── More by this Designer ───────────────────────── */}
      {template.more_by_designer && template.more_by_designer.length > 0 && (
        <div style={{ marginBottom: '64px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>More by {template.designer_name}</h3>
            <Link
              to={`/designer/${template.designer_name}`}
              style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--primary-text)', textDecoration: 'none' }}
            >
              View Studio Portfolio →
            </Link>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '20px'
          }}>
            {template.more_by_designer.map(item => (
              <Link
                key={item.id}
                to={`/template/${item.slug || item.id}`}
                className="interactive-card"
                style={{
                  textDecoration: 'none',
                  background: 'var(--bg-surface)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{ aspectRatio: '16/10', overflow: 'hidden', background: '#000' }}>
                  <img
                    src={item.preview_url || PLACEHOLDER}
                    alt={item.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)', marginBottom: '8px' }}>
                    {item.title}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, color: (item.is_free || !item.price_cents) ? 'var(--accent)' : 'var(--text-primary)', fontSize: '0.875rem' }}>
                      {(item.is_free || !item.price_cents) ? 'Free' : `$${(item.price_cents / 100).toFixed(2)}`}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      ★ {item.avg_rating || 5.0}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={`Share ${template.title}`}
      />

      {/* ── Merge Request Modal ── */}
      {mrModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(5, 5, 10, 0.85)',
          backdropFilter: 'blur(12px)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          animation: 'fadeIn 0.2s ease'
        }} onClick={() => setMrModalOpen(false)}>
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-medium)',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '520px',
            padding: '28px',
            boxShadow: 'var(--shadow-xl)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ padding: '8px', borderRadius: '12px', background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
                  <GitPullRequest size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800 }}>Submit Merge Request</h3>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Propose vector fixes, variants, or upgrades</span>
                </div>
              </div>
              <button onClick={() => setMrModalOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitMr} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Proposal Title <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Added Figma Tokens & Dark Mode Auto-Layout"
                  value={mrForm.title}
                  onChange={e => setMrForm({ ...mrForm, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    background: 'var(--bg-base)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Improvement Summary & Motivation
                </label>
                <textarea
                  placeholder="Explain why this improvement is valuable and how it helps other creators..."
                  value={mrForm.description}
                  onChange={e => setMrForm({ ...mrForm, description: e.target.value })}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    background: 'var(--bg-base)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Diff Notes / Cloud File Links
                </label>
                <textarea
                  placeholder="Link to your updated Figma file, Behance draft, or list of modified vector layers..."
                  value={mrForm.diff_notes}
                  onChange={e => setMrForm({ ...mrForm, diff_notes: e.target.value })}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    background: 'var(--bg-base)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '6px' }}>
                <Button variant="ghost" type="button" onClick={() => setMrModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={submittingMr}>
                  {submittingMr ? 'Submitting...' : '🚀 Submit to Author'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Community Comments ─────────────────────────── */}
      <CommentSection templateId={template.id} />
    </div>
  );
}
